/*
 * Event reminders, 15 minutes before an event the reader RSVP'd to.
 *
 * - Android app: a real system notification via @capacitor/local-notifications,
 *   delivered even when the app is closed. Tapping it opens the event.
 * - Web: browsers can't wake a closed site at a set time, so the reminder
 *   fires while BAWS is open in a tab (timers re-armed on every visit, and a
 *   missed reminder is shown on the next visit if the event hasn't started).
 *   "Add to calendar" on the card is the reliable fallback on the web.
 *
 * A reminder record: { kind: 'native'|'web'|'none', at (ms), reason? , notified? }
 */
import { Capacitor } from '@capacitor/core';

export const LEAD_MS = 15 * 60 * 1000;
const MAX_TIMEOUT = 2147483647; // setTimeout limit (~24.8 days)

const isNative = () => Capacitor.isNativePlatform();

// Local notification ids must be 32-bit ints; derive one from the item id.
export const notificationId = (itemId) => {
  let h = 0;
  for (let i = 0; i < itemId.length; i++)
    h = (Math.imul(31, h) + itemId.charCodeAt(i)) | 0;
  return Math.abs(h) % 2000000000 || 1;
};

/** When to remind: 15 min before start; soon if that's already passed. */
export function reminderTime(item, now = Date.now()) {
  const start = item?.event?.start;
  if (!start || start <= now) return null;
  return Math.max(start - LEAD_MS, now + 5000);
}

const timeText = (ms) =>
  new Date(ms).toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

const content = (item) => ({
  title: `Starting soon: ${item.title}`,
  body: [
    `Starts ${timeText(item.event.start)}`,
    item.event.location || (item.event.joinUrl ? 'Online' : ''),
  ]
    .filter(Boolean)
    .join(' · '),
  link: item.event.joinUrl || item.link || '/feed',
});

const loadPlugin = () =>
  import('@capacitor/local-notifications').then((m) => m.LocalNotifications);

export async function scheduleReminder(item) {
  const at = reminderTime(item);
  if (!at) return { kind: 'none', reason: 'started' };

  if (isNative()) {
    try {
      const LocalNotifications = await loadPlugin();
      let perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted')
        perm = await LocalNotifications.requestPermissions();
      if (perm.display !== 'granted')
        return { kind: 'none', reason: 'permission', at };
      const c = content(item);
      await LocalNotifications.schedule({
        notifications: [
          {
            id: notificationId(item.id),
            title: c.title,
            body: c.body,
            schedule: { at: new Date(at), allowWhileIdle: true },
            extra: { link: c.link },
          },
        ],
      });
      return { kind: 'native', at };
    } catch (e) {
      return { kind: 'none', reason: 'error', at };
    }
  }

  if (!('Notification' in window))
    return { kind: 'none', reason: 'unsupported', at };
  let perm = Notification.permission;
  if (perm === 'default') {
    try {
      perm = await Notification.requestPermission();
    } catch (e) {
      perm = 'denied';
    }
  }
  if (perm !== 'granted') return { kind: 'none', reason: 'permission', at };
  return { kind: 'web', at };
}

const timers = new Map();

export async function cancelReminder(item) {
  clearTimeout(timers.get(item.id));
  timers.delete(item.id);
  if (isNative()) {
    try {
      const LocalNotifications = await loadPlugin();
      await LocalNotifications.cancel({
        notifications: [{ id: notificationId(item.id) }],
      });
    } catch (e) {
      // nothing scheduled
    }
  }
}

async function showWebNotification(item) {
  const c = content(item);
  const options = {
    body: c.body,
    icon: '/logo192.png',
    tag: `event-${item.id}`,
    data: { url: c.link },
  };
  try {
    const n = new Notification(c.title, options);
    n.onclick = () => {
      window.focus();
      if (/^https?:\/\//.test(c.link))
        window.open(c.link, '_blank', 'noopener');
      else window.location.assign(c.link);
    };
    return;
  } catch (e) {
    // Android Chrome: page notifications must go through the service worker.
  }
  const reg = await navigator.serviceWorker?.getRegistration?.();
  if (reg) await reg.showNotification(c.title, options);
}

/**
 * Web only: (re)arm timers for the given RSVPs. `onFired(id)` is called
 * after a reminder is shown so it isn't shown again.
 */
export function armWebReminders(entries, onFired, now = Date.now()) {
  if (
    isNative() ||
    !('Notification' in window) ||
    Notification.permission !== 'granted'
  )
    return;
  timers.forEach((t) => clearTimeout(t));
  timers.clear();
  entries.forEach(({ item, reminder }) => {
    if (reminder?.kind !== 'web' || reminder.notified) return;
    if (!item.event || item.event.start <= now) return; // started: too late
    const fire = async () => {
      timers.delete(item.id);
      await showWebNotification(item);
      onFired(item.id);
    };
    const delay = reminder.at - now;
    if (delay <= 0)
      fire(); // missed while the site was closed
    else if (delay < MAX_TIMEOUT) timers.set(item.id, setTimeout(fire, delay));
  });
}

/** Native only: open the event when a reminder notification is tapped. */
export async function listenForReminderTaps(navigate) {
  if (!isNative()) return () => {};
  const LocalNotifications = await loadPlugin();
  const handle = await LocalNotifications.addListener(
    'localNotificationActionPerformed',
    ({ notification }) => {
      const link = notification?.extra?.link;
      if (!link) return;
      if (/^https?:\/\//.test(link)) window.open(link, '_blank');
      else navigate(link);
    }
  );
  return () => handle.remove();
}

// "Add to calendar": Google Calendar link (works on web and Android).
const gcalDate = (ms) =>
  new Date(ms)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
export function googleCalendarUrl(item) {
  const ev = item.event;
  const details = [
    item.summary,
    ev.joinUrl && `Join: ${ev.joinUrl}`,
    'Shared on BAWS.in',
  ]
    .filter(Boolean)
    .join('\n\n');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: item.title,
    dates: `${gcalDate(ev.start)}/${gcalDate(ev.end)}`,
    details,
    location: ev.location || ev.joinUrl || '',
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

// .ics file (web): any calendar app, with a 15-minute alarm built in.
export function icsDataUrl(item) {
  const ev = item.event;
  const esc = (s) =>
    String(s || '')
      .replace(/[\\,;]/g, (m) => `\\${m}`)
      .replace(/\n/g, '\\n');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BAWS.in//Feed//EN',
    'BEGIN:VEVENT',
    `UID:${item.id.replace(/[^\w-]/g, '')}@baws.in`,
    `DTSTAMP:${gcalDate(Date.now())}`,
    `DTSTART:${gcalDate(ev.start)}`,
    `DTEND:${gcalDate(ev.end)}`,
    `SUMMARY:${esc(item.title)}`,
    `DESCRIPTION:${esc([item.summary, ev.joinUrl && `Join: ${ev.joinUrl}`].filter(Boolean).join('\n'))}`,
    `LOCATION:${esc(ev.location || ev.joinUrl || '')}`,
    ev.joinUrl ? `URL:${ev.joinUrl}` : null,
    'BEGIN:VALARM',
    'TRIGGER:-PT15M',
    'ACTION:DISPLAY',
    `DESCRIPTION:${esc(item.title)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`;
}
